import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import ThemeLink from './components/ThemeLink';
import useThemeNavigate from './hooks/useThemeNavigate';

/**
 * RouterContext
 * Provides a context for components to access theme-aware navigation utilities
 */
export const RouterContext = React.createContext({
  Link: ThemeLink,
  navigate: () => {}
});

/**
 * RouterProvider
 * Provider component that makes theme-aware routing utilities available to child components
 */
export const RouterProvider = ({ children }) => {
  const navigate = useThemeNavigate();
  const location = useLocation();
  
  // Memoize the context value to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    Link: ThemeLink,
    navigate,
    location
  }), [navigate, location]);
  
  return (
    <RouterContext.Provider value={value}>
      {children}
    </RouterContext.Provider>
  );
};

/**
 * useRouter hook
 * Custom hook to access theme-aware routing utilities
 */
export const useRouter = () => React.useContext(RouterContext);

/**
 * ThemeRouterWrapper component
 * Wraps the application with the router provider and browser router
 */
const ThemeRouterWrapper = ({ children, basename }) => {
  return (
    <BrowserRouter 
      basename={basename || '/'}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <RouterContent>
        {children}
      </RouterContent>
    </BrowserRouter>
  );
};

/**
 * RouterContent
 * A separate component for the router content to prevent re-renders
 */
const RouterContent = ({ children }) => {
  return (
    <Routes>
      <Route path="/*" element={
        <RouterProvider>
          {children}
        </RouterProvider>
      } />
    </Routes>
  );
};

export default ThemeRouterWrapper; 